import { Link, Typography } from "@hellocarbo/lasagne";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";

import bronzeMedal from "@/assets/fr/medals/simple/bronze.png";
import diamondMedal from "@/assets/fr/medals/simple/diamond.png";
import goldMedal from "@/assets/fr/medals/simple/gold.png";
import noMedal from "@/assets/fr/medals/simple/no-medal.svg";
import silverMedal from "@/assets/fr/medals/simple/silver.png";
import { UserContext } from "@/contexts";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import { useCommunicateContext } from "@/pages/Communicate";

import BaseCard, { ActionButton } from "./BaseCard";

export const medalUrlMap = {
  bronze: { simple: bronzeMedal },
  silver: { simple: silverMedal },
  gold: { simple: goldMedal },
  diamond: { simple: diamondMedal },
};

export const medalPercentileMap = {
  bronze: 30,
  silver: 15,
  gold: 5,
  diamond: 1,
};

const Medal = () => {
  const {
    medalsData: { medal, season, season_year, any_medals },
  } = useCommunicateContext();
  const { t } = useTranslation("communicate");
  const { user } = use(UserContext);

  const downloadImage = () => {
    if (medal) {
      saveAs(medalUrlMap[medal].simple, "medal.png");
    }
  };

  const { sendSegmentEvent } = useSegmentEvent();

  if (!medal && any_medals) {
    return (
      <BaseCard
        title={t("medal.withoutMedal.title")}
        text={t("medal.withoutMedal.text")}
        Icon={() => (
          <img
            src={noMedal}
            alt={t("medal.withoutMedal.IllustrationAlt")}
            className="size-[176px]"
          />
        )}
      >
        <Typography className="mb-sm mt-sm">
          {t("medal.withoutMedal.rankText", {
            season: t(`medal.seasons.${season}`),
            year: season_year,
          })}
        </Typography>

        <div className="mt-sm flex justify-between">
          <Link
            href="https://hellocarbo-25361575.hs-sites-eu1.com/knowledge/medaille-carbo"
            className="my-sm"
            shouldOpenNewTab
          >
            {t("medal.helpCenterLink")}
          </Link>
        </div>
      </BaseCard>
    );
  } else if (medal) {
    return (
      <BaseCard
        title={t("medal.withMedal.title")}
        text={t("medal.withMedal.text", {
          percentile: medalPercentileMap[medal],
        })}
        Icon={() => (
          <img
            src={medalUrlMap[medal].simple}
            alt={`${user?.company.name} - ${t("medal.withMedal.title")}`}
            className="size-[176px]"
          />
        )}
        showNewTag
      >
        <Typography className="mb-sm mt-sm">
          {t("medal.withMedal.rankText", {
            season: t(`medal.seasons.${season}`),
            year: season_year,
            percentile: medalPercentileMap[medal],
          })}
        </Typography>
        <ActionButton
          onClick={() => {
            downloadImage();
            sendSegmentEvent("carbo_medal_downloaded");
          }}
        >
          <Download />
          {t("medal.withMedal.download")}
        </ActionButton>

        <div className="mt-sm flex justify-between">
          <Link
            href="https://hellocarbo-25361575.hs-sites-eu1.com/knowledge/medaille-carbo"
            className="my-sm"
            shouldOpenNewTab
          >
            {t("medal.helpCenterLink")}
          </Link>
        </div>
      </BaseCard>
    );
  } else {
    return null;
  }
};

export default Medal;
